import React, {
   useCallback,
   useContext,
   useEffect,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import { createPortal } from "react-dom";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../assets/svg/arrow-s.svg";
import editIcon from "../../assets/svg/edit.svg";
import messageIcon from "../../assets/svg/tabler--message.svg";
import trashIcon from "../../assets/svg/trash.svg";
import {
   deleteAdminRating,
   getAdminRatingsAll,
   updateAdminRating,
} from "../../api/instructorRatingsService";
import {
   canDeleteAdminReviews,
   canEditAdminReviews,
   canReplyToAdminReviews,
   canViewAdminReviews,
   reviewsReplyIsGroupScoped,
} from "../../auth/reviewsAccess";
import { getManagerGroups } from "../../api/groupsService";
import { getUsers } from "../../api/usersService";
import { readStudentGroupId, unwrapList } from "../../utils/groupFields";
import { useResolvedInstructor } from "../../hooks/useResolvedInstructor";
import RatingStarsRow from "../Common/RatingStarsRow";
import StudentRatingPicker from "../Common/StudentRatingPicker";
import { closePopup } from "../Common/popupUIStore";
import {
   openStaffChatReplyToReview,
   readReviewStudentId,
} from "../../stores/staffChatWorkspaceStore";
import {
   getAdminReviewsLastSeenAt,
   markAdminReviewsSeen,
} from "../../utils/adminReviewsSeen";
import { UserContext } from "../../UserContext";

const PAGE_SIZE = 20;

function ReviewActionIcon({ src, className = "" }) {
   return (
      <ReactSVG
         className={`adminReviewsPopup__actionIcon ${className}`.trim()}
         src={src}
      />
   );
}

function formatRatingNumber(value) {
   const number = Number(value);
   if (!Number.isFinite(number)) return "—";
   return number % 1 === 0 ? String(number) : number.toFixed(1);
}

function getPersonName(person, fallback = "—") {
   if (!person) return fallback;
   const first = String(person.firstName || "").trim();
   const last = String(person.lastName || "").trim();
   const full = `${first} ${last}`.trim();
   return full || person.email || fallback;
}

function getRatingDateLabel(rating) {
   const raw =
      rating?.createdAt ||
      rating?.updatedAt ||
      rating?.reservation?.startTime ||
      rating?.reservation?.date;
   if (!raw) return "";

   const date = new Date(raw);
   if (Number.isNaN(date.getTime())) return "";

   return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
   });
}

function getRatingSortMs(rating) {
   const raw =
      rating?.createdAt ||
      rating?.updatedAt ||
      rating?.reservation?.startTime ||
      0;
   const ms = new Date(raw).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function getRatingValue(rating) {
   const value = Math.round(Number(rating?.rating));
   return Number.isFinite(value) ? value : 0;
}

function getRatingCreatedMs(rating) {
   const raw = rating?.createdAt || rating?.updatedAt || 0;
   const ms = new Date(raw).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function getRatingId(rating) {
   return (
      rating?.id ??
      `${rating?.reservationId || "r"}-${rating?.userId || "u"}-${rating?.createdAt || "c"}`
   );
}

function AdminReviewsOverlayPortal({ hostRef, children }) {
   const [host, setHost] = useState(null);

   useLayoutEffect(() => {
      setHost(hostRef.current?.closest(".popupPanelUI") || null);
   }, [hostRef]);

   const content = React.Children.toArray(children).filter(Boolean);
   if (!content.length) return null;
   // Dacă nu găsim host-ul (rar, pe unele layout-uri), renderizăm direct ca să
   // nu mai rămână overlay-ul invizibil.
   if (!host) return <>{content}</>;
   return createPortal(content, host);
}

function ReviewEditOverlay({
   rating,
   canDelete,
   editRating,
   editHoverRating,
   editComment,
   saving,
   deletingId,
   error,
   onClose,
   onSave,
   onDelete,
   onEditRatingChange,
   onEditHoverRatingChange,
   onEditCommentChange,
}) {
   const [isVisible, setIsVisible] = useState(false);
   const studentName = getPersonName(rating?.user, "Elev necunoscut");
   const instructorName = getPersonName(rating?.instructor, "Necunoscut");
   const dateLabel = getRatingDateLabel(rating);
   const isDeleting = Number(deletingId) === Number(rating?.id);

   useEffect(() => {
      const frame = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(frame);
   }, []);

   useEffect(() => {
      const handleKeyDown = (event) => {
         if (event.key === "Escape" && !saving) onClose();
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
   }, [onClose, saving]);

   const handleBackdropClose = () => {
      if (saving) return;
      onClose();
   };

   return (
      <div
         className={`adminReviewsPopup__detailOverlay${
            isVisible ? " is-open" : ""
         }`}
         role="dialog"
         aria-modal="true"
         aria-labelledby="adminReviewsEditTitle"
         onClick={handleBackdropClose}
      >
         <div
            className="adminReviewsPopup__detailPanel"
            onClick={(event) => event.stopPropagation()}
         >
            <div className="adminReviewsPopup__detailHeader adminReviewsPopup__detailHeader--split">
               <button
                  type="button"
                  className="adminReviewsPopup__button adminReviewsPopup__button--secondary adminReviewsPopup__detailClose"
                  onClick={onClose}
                  disabled={saving}
               >
                  Înapoi
               </button>

               <div className="adminReviewsPopup__detailHeaderActions">
                  {canDelete ? (
                     <button
                        type="button"
                        className="adminReviewsPopup__button adminReviewsPopup__button--danger"
                        onClick={() => onDelete(rating)}
                        disabled={saving || isDeleting}
                     >
                        {isDeleting ? "..." : "Șterge"}
                     </button>
                  ) : null}

                  <button
                     type="button"
                     className="adminReviewsPopup__button adminReviewsPopup__button--primary"
                     disabled={saving}
                     onClick={onSave}
                  >
                     {saving ? "Se salvează..." : "Salvează"}
                  </button>
               </div>
            </div>

            <div className="adminReviewsPopup__detailHero">
               <h4
                  id="adminReviewsEditTitle"
                  className="adminReviewsPopup__detailTitle"
               >
                  {studentName}
               </h4>

               <div className="adminReviewsPopup__badges">
                  <span className="adminReviewsPopup__badge">
                     Cu {instructorName}
                  </span>
                  {dateLabel ? (
                     <span className="adminReviewsPopup__badge adminReviewsPopup__badge--accent">
                        {dateLabel}
                     </span>
                  ) : null}
               </div>
            </div>

            <form
               id="adminReviewsEditForm"
               className="adminReviewsPopup__detailForm"
               onSubmit={onSave}
            >
               <div className="adminReviewsPopup__field adminReviewsPopup__field--rating">
                  <span className="adminReviewsPopup__fieldLabel">Rating</span>
                  <StudentRatingPicker
                     value={editRating}
                     previewValue={editHoverRating}
                     onChange={onEditRatingChange}
                     onPreviewChange={onEditHoverRatingChange}
                     disabled={saving}
                  />
               </div>

               <label className="adminReviewsPopup__field adminReviewsPopup__field--comment">
                  <span className="adminReviewsPopup__fieldLabel">
                     Comentariu{" "}
                     {editRating >= 1 && editRating <= 3
                        ? "(obligatoriu)"
                        : "(opțional)"}
                  </span>
                  <textarea
                     className="adminReviewsPopup__input adminReviewsPopup__input--textarea"
                     value={editComment}
                     onChange={(event) => onEditCommentChange(event.target.value)}
                     placeholder={
                        editRating >= 1 && editRating <= 3
                           ? "Spune-ne ce nu a mers bine la lecție..."
                           : "Scrie un comentariu..."
                     }
                     rows={4}
                     disabled={saving}
                  />
               </label>
            </form>

            {error ? (
               <p className="adminReviewsPopup__formError adminReviewsPopup__formError--overlay">
                  {error}
               </p>
            ) : null}
         </div>
      </div>
   );
}

function ReviewDetailOverlay({
   rating,
   canReply: canReplyProp = false,
   canEdit,
   canDelete,
   isNew,
   deletingId,
   onClose,
   onEdit,
   onDelete,
   onReply,
}) {
   const [isVisible, setIsVisible] = useState(false);
   const comment = String(rating?.comment || "").trim();
   const studentName = getPersonName(rating?.user, "Elev necunoscut");
   const instructorName = getPersonName(rating?.instructor, "Necunoscut");
   const dateLabel = getRatingDateLabel(rating);
   const starValue = getRatingValue(rating);
   const isDeleting = Number(deletingId) === Number(rating?.id);
   // Raspunsul cere si dreptul de a scrie in Mesaje, si un elev identificabil.
   // Id-ul poate veni plat (`userId`), nu doar din `user` embedat.
   const canReply = canReplyProp && readReviewStudentId(rating) != null;

   useEffect(() => {
      const frame = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(frame);
   }, []);

   useEffect(() => {
      const handleKeyDown = (event) => {
         if (event.key === "Escape") onClose();
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
   }, [onClose]);

   return (
      <div
         className={`adminReviewsPopup__detailOverlay${
            isVisible ? " is-open" : ""
         }`}
         role="dialog"
         aria-modal="true"
         aria-labelledby="adminReviewsDetailTitle"
         onClick={onClose}
      >
         <div
            className="adminReviewsPopup__detailPanel"
            onClick={(event) => event.stopPropagation()}
         >
            <div className="adminReviewsPopup__detailHeader">
               <button
                  type="button"
                  className="adminReviewsPopup__button adminReviewsPopup__button--secondary adminReviewsPopup__detailClose"
                  onClick={onClose}
               >
                  Închide
               </button>
            </div>

            <div className="adminReviewsPopup__detailHero">
               <h4
                  id="adminReviewsDetailTitle"
                  className="adminReviewsPopup__detailTitle"
               >
                  {studentName}
               </h4>

               <div className="adminReviewsPopup__badges">
                  <span className="adminReviewsPopup__badge">
                     {formatRatingNumber(starValue)}{" "}
                     {starValue === 1 ? "stea" : "stele"}
                  </span>
                  <span className="adminReviewsPopup__badge">
                     Cu {instructorName}
                  </span>
                  {isNew ? (
                     <span className="adminReviewsPopup__badge adminReviewsPopup__badge--new">
                        Nou
                     </span>
                  ) : null}
                  {dateLabel ? (
                     <span className="adminReviewsPopup__badge adminReviewsPopup__badge--accent">
                        {dateLabel}
                     </span>
                  ) : null}
               </div>

               <div className="adminReviewsPopup__detailStars">
                  <RatingStarsRow value={starValue} max={5} />
               </div>
            </div>

            <div className="adminReviewsPopup__detailBody">
               <span className="adminReviewsPopup__fieldLabel">Comentariu</span>
               <p
                  className={`adminReviewsPopup__detailComment${
                     comment ? "" : " is-empty"
                  }`}
               >
                  {comment || "Fără comentariu"}
               </p>
            </div>

            {canReply || canEdit || canDelete ? (
               <div
                  className={
                     "adminReviewsPopup__detailActions" +
                     (canReply && canEdit && canDelete
                        ? " adminReviewsPopup__detailActions--triple"
                        : "")
                  }
               >
                  {canReply ? (
                     <button
                        type="button"
                        className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--reply"
                        onClick={() => onReply?.(rating)}
                        title="Răspunde în Mesaje"
                        aria-label={`Răspunde lui ${studentName} în Mesaje`}
                     >
                        <ReviewActionIcon src={messageIcon} />
                     </button>
                  ) : null}

                  {canEdit ? (
                     <button
                        type="button"
                        className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--edit"
                        onClick={() => onEdit(rating)}
                        title="Editează"
                        aria-label={`Editează recenzia de la ${studentName}`}
                     >
                        <ReviewActionIcon src={editIcon} />
                     </button>
                  ) : null}

                  {canDelete ? (
                     <button
                        type="button"
                        className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--danger"
                        onClick={() => onDelete(rating)}
                        disabled={isDeleting}
                        title="Șterge"
                        aria-label={`Șterge recenzia de la ${studentName}`}
                     >
                        <ReviewActionIcon src={trashIcon} />
                     </button>
                  ) : null}
               </div>
            ) : null}
         </div>
      </div>
   );
}

function ReviewCard({
   rating,
   canReply: canReplyProp = false,
   canEdit,
   canDelete,
   onOpen,
   onEdit,
   onDelete,
   onReply,
   deletingId,
   isNew,
}) {
   const comment = String(rating?.comment || "").trim();
   const studentName = getPersonName(rating?.user, "Elev necunoscut");
   const instructorName = getPersonName(rating?.instructor, "Necunoscut");
   const dateLabel = getRatingDateLabel(rating);
   const starValue = getRatingValue(rating);
   const isDeleting = Number(deletingId) === Number(rating?.id);
   // Raspunsul cere si dreptul de a scrie in Mesaje, si un elev identificabil.
   // Id-ul poate veni plat (`userId`), nu doar din `user` embedat.
   const canReply = canReplyProp && readReviewStudentId(rating) != null;

   const handleOpen = () => onOpen?.(rating);

   const handleKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      handleOpen();
   };

   return (
      <article className="adminReviewsPopup__card">
         <div
            className="adminReviewsPopup__cardMain"
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={handleKeyDown}
            aria-label={`Deschide recenzia de la ${studentName}`}
         >
            <div className="adminReviewsPopup__cardHead">
               <div className="adminReviewsPopup__badges">
                  <span className="adminReviewsPopup__badge">
                     {formatRatingNumber(starValue)}{" "}
                     {starValue === 1 ? "stea" : "stele"}
                  </span>
               </div>
               <div className="adminReviewsPopup__dateGroup">
                  {isNew ? (
                     <span className="adminReviewsPopup__badge adminReviewsPopup__badge--new">
                        Nou
                     </span>
                  ) : null}
                  {dateLabel ? (
                     <span className="adminReviewsPopup__badge adminReviewsPopup__badge--accent">
                        {dateLabel}
                     </span>
                  ) : null}
               </div>
            </div>

            <div className="adminReviewsPopup__cardRows">
               <div className="adminReviewsPopup__cardRow">
                  <span className="adminReviewsPopup__fieldLabel">Elev</span>
                  <span className="adminReviewsPopup__cardValue">
                     {studentName}
                  </span>
               </div>

               <div className="adminReviewsPopup__cardRow">
                  <span className="adminReviewsPopup__fieldLabel">
                     Instructor
                  </span>
                  <span className="adminReviewsPopup__cardValue">
                     {instructorName}
                  </span>
               </div>

               <div className="adminReviewsPopup__cardRow">
                  <span className="adminReviewsPopup__fieldLabel">
                     Comentariu
                  </span>
                  <span
                     className={`adminReviewsPopup__cardValue adminReviewsPopup__cardValue--comment${
                        comment ? "" : " is-empty"
                     }`}
                  >
                     {comment || "Fără comentariu"}
                  </span>
               </div>
            </div>
         </div>

         {canReply || canEdit || canDelete ? (
            <div
               className={
                  "adminReviewsPopup__actions" +
                  (canReply && canEdit && canDelete
                     ? " adminReviewsPopup__actions--triple"
                     : "")
               }
            >
               {canReply ? (
                  <button
                     type="button"
                     className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--reply"
                     onClick={() => onReply?.(rating)}
                     title="Răspunde în Mesaje"
                     aria-label={`Răspunde lui ${studentName} în Mesaje`}
                  >
                     <ReviewActionIcon src={messageIcon} />
                  </button>
               ) : null}

               {canEdit ? (
                  <button
                     type="button"
                     className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--edit"
                     onClick={() => onEdit(rating)}
                     title="Editează"
                     aria-label={`Editează recenzia de la ${studentName}`}
                  >
                     <ReviewActionIcon src={editIcon} />
                  </button>
               ) : null}

               {canDelete ? (
                  <button
                     type="button"
                     className="adminReviewsPopup__button adminReviewsPopup__button--icon adminReviewsPopup__button--danger"
                     onClick={() => onDelete(rating)}
                     disabled={isDeleting}
                     title="Șterge"
                     aria-label={`Șterge recenzia de la ${studentName}`}
                  >
                     <ReviewActionIcon src={trashIcon} />
                  </button>
               ) : null}
            </div>
         ) : null}
      </article>
   );
}

export default function AdminReviewsPopup() {
   const { user } = useContext(UserContext);
   // Instructorul SUPERIOR vede recenziile ca managerul, deci accesul nu mai
   // depinde doar de rol: trebuie sa stim si tipul de instructor.
   const { instructor, resolved: viewerResolved } = useResolvedInstructor();
   const canDelete = canDeleteAdminReviews(user);
   const canEdit = canEditAdminReviews(user);
   const canReply = canReplyToAdminReviews(user);
   const canAccess = canViewAdminReviews(user, instructor);
   const replyIsGroupScoped = reviewsReplyIsGroupScoped(user);

   const [ratings, setRatings] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [starFilters, setStarFilters] = useState([]);
   const [hoverFilter, setHoverFilter] = useState(0);
   const [commentFilter, setCommentFilter] = useState("all"); // all | with | without
   const [newestFirst, setNewestFirst] = useState(true);
   const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
   const [deletingId, setDeletingId] = useState(null);
   const [editingRating, setEditingRating] = useState(null);
   const [editRating, setEditRating] = useState(0);
   const [editHoverRating, setEditHoverRating] = useState(0);
   const [editComment, setEditComment] = useState("");
   const [saving, setSaving] = useState(false);
   const [seenBaselineMs, setSeenBaselineMs] = useState(null);
   const [viewingRating, setViewingRating] = useState(null);
   const baselineCapturedRef = useRef(false);
   /** Set<string> de id-uri de elevi din grupele managerului; null = neaplicabil. */
   const [replyStudentIds, setReplyStudentIds] = useState(null);
   const [replyScopeReady, setReplyScopeReady] = useState(!replyIsGroupScoped);
   /** Grupele managerului, pentru cazul in care recenzia aduce grupa embedata. */
   const replyGroupIdsRef = useRef(null);

   const loadRatings = useCallback(async () => {
      // Pentru un instructor, tipul vine dintr-un fetch: pana se rezolva nu
      // putem decide accesul, iar un "Nu exista recenzii." intermediar ar fi
      // fals. Ramanem pe starea de incarcare.
      if (!viewerResolved) {
         setLoading(true);
         return;
      }

      if (!canAccess) {
         setRatings([]);
         setLoading(false);
         return;
      }

      setLoading(true);
      setError("");

      try {
         const rows = await getAdminRatingsAll();
         setRatings(Array.isArray(rows) ? rows : []);
      } catch (err) {
         setRatings([]);
         setError(err?.message || "Nu am putut încărca recenziile.");
      } finally {
         setLoading(false);
      }
   }, [canAccess, viewerResolved]);

   useEffect(() => {
      loadRatings();
   }, [loadRatings]);

   useEffect(() => {
      if (!viewerResolved || !canAccess || !user?.id) return;
      if (!baselineCapturedRef.current) {
         baselineCapturedRef.current = true;
         setSeenBaselineMs(getAdminReviewsLastSeenAt(user.id));
      }
      markAdminReviewsSeen(user.id);
   }, [canAccess, user?.id, viewerResolved]);

   // Aceeasi sursa de adevar ca in chat: grupele managerului + grupa fiecarui
   // elev din /users. Fara ea nu putem sti daca „Raspunde" ar reusi.
   useEffect(() => {
      if (!replyIsGroupScoped) {
         replyGroupIdsRef.current = null;
         setReplyStudentIds(null);
         setReplyScopeReady(true);
         return undefined;
      }

      if (!canAccess || !user?.id) {
         replyGroupIdsRef.current = new Set();
         setReplyStudentIds(new Set());
         setReplyScopeReady(true);
         return undefined;
      }

      let cancelled = false;
      setReplyScopeReady(false);

      (async () => {
         try {
            const [groups, users] = await Promise.all([
               getManagerGroups(user.id),
               getUsers(),
            ]);
            if (cancelled) return;

            const groupIds = new Set(
               (groups || [])
                  .map((group) => String(group?.id ?? group?.groupId ?? ""))
                  .filter(Boolean),
            );

            replyGroupIdsRef.current = groupIds;

            const allowed = new Set();
            for (const student of unwrapList(users) || []) {
               if (student?.id == null) continue;
               const groupId = readStudentGroupId(student);
               if (groupId == null) continue;
               if (!groupIds.has(String(groupId))) continue;
               allowed.add(String(student.id));
            }
            setReplyStudentIds(allowed);
         } catch {
            // Scope necunoscut: ascundem butonul in loc sa lasam managerul sa
            // dea intr-o eroare de permisiuni la trimitere.
            if (!cancelled) {
               replyGroupIdsRef.current = new Set();
               setReplyStudentIds(new Set());
            }
         } finally {
            if (!cancelled) setReplyScopeReady(true);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [replyIsGroupScoped, canAccess, user?.id]);

   const canReplyToRating = useCallback(
      (rating) => {
         if (!canReply) return false;
         if (!replyIsGroupScoped) return true;
         if (!replyScopeReady) return false;

         const studentId = readReviewStudentId(rating);
         if (studentId == null) return false;
         if (replyStudentIds?.has(String(studentId))) return true;

         // Daca recenzia aduce grupa direct, o acceptam fara lista de elevi.
         const embeddedGroupId = readStudentGroupId(
            rating?.user ?? rating?.student,
         );
         if (embeddedGroupId == null) return false;
         return Boolean(replyGroupIdsRef.current?.has(String(embeddedGroupId)));
      },
      [canReply, replyIsGroupScoped, replyScopeReady, replyStudentIds],
   );

   const filteredRatings = useMemo(() => {
      const source = Array.isArray(ratings) ? ratings : [];
      return source.filter((rating) => {
         if (starFilters.length) {
            const allowed = new Set(starFilters.map((value) => Number(value)));
            if (!allowed.has(getRatingValue(rating))) return false;
         }

         if (commentFilter === "with") {
            return Boolean(String(rating?.comment || "").trim());
         }
         if (commentFilter === "without") {
            return !String(rating?.comment || "").trim();
         }
         return true;
      });
   }, [ratings, starFilters, commentFilter]);

   const sortedRatings = useMemo(() => {
      const clone = [...filteredRatings];
      clone.sort((a, b) => {
         const diff = getRatingSortMs(a) - getRatingSortMs(b);
         return newestFirst ? -diff : diff;
      });
      return clone;
   }, [filteredRatings, newestFirst]);

   useEffect(() => {
      setVisibleCount(PAGE_SIZE);
   }, [starFilters, newestFirst, commentFilter, sortedRatings.length]);

   const visibleRatings = useMemo(
      () => sortedRatings.slice(0, visibleCount),
      [sortedRatings, visibleCount],
   );

   const hasMore = visibleCount < sortedRatings.length;

   const filterLabel = useMemo(() => {
      const parts = [];
      if (starFilters.length) parts.push(`${starFilters.join(", ")} stele`);
      if (commentFilter === "with") parts.push("cu text");
      if (commentFilter === "without") parts.push("fără text");
      return parts.join(" · ");
   }, [starFilters, commentFilter]);

   const openView = (rating) => {
      setViewingRating(rating);
      setError("");
   };

   const closeView = () => {
      setViewingRating(null);
   };

   const handleReplyToReview = useCallback(
      (rating) => {
         if (!canReplyToRating(rating)) return;
         const ok = openStaffChatReplyToReview(rating);
         if (!ok) {
            // Cu fallback-ul pe `userId`, aici ajungem doar cand randul nu are
            // deloc elevul - o problema de date, nu una trecatoare.
            setError(
               "Recenzia nu are elevul asociat, așa că nu putem deschide conversația.",
            );
            return;
         }
         setViewingRating(null);
         closePopup();
      },
      [canReplyToRating],
   );

   const openEdit = (rating) => {
      if (!canEdit) return;
      setViewingRating(null);
      setEditingRating(rating);
      setEditRating(getRatingValue(rating));
      setEditHoverRating(0);
      setEditComment(String(rating?.comment || ""));
      setError("");
   };

   const closeEdit = () => {
      setEditingRating(null);
      setEditRating(0);
      setEditHoverRating(0);
      setEditComment("");
      setError("");
   };

   const handleDelete = async (rating) => {
      const ratingId = Number(rating?.id);
      if (!canDelete || !Number.isFinite(ratingId) || deletingId) return;

      const studentName = getPersonName(rating?.user, "elevul");
      const confirmed = window.confirm(
         `Ștergi recenzia lăsată de ${studentName}?`,
      );
      if (!confirmed) return;

      setDeletingId(ratingId);
      setError("");

      try {
         await deleteAdminRating(ratingId);
         setRatings((current) =>
            current.filter((item) => Number(item?.id) !== ratingId),
         );
         if (Number(viewingRating?.id) === ratingId) {
            closeView();
         }
         if (Number(editingRating?.id) === ratingId) {
            closeEdit();
         }
      } catch (err) {
         setError(err?.message || "Nu am putut șterge recenzia.");
      } finally {
         setDeletingId(null);
      }
   };

   const handleSaveEdit = async (event) => {
      event.preventDefault();
      const ratingId = Number(editingRating?.id);
      if (!Number.isFinite(ratingId) || saving) return;

      if (!editRating) {
         setError("Selectează un rating de la 1 la 5 stele.");
         return;
      }

      const requiresComment = editRating >= 1 && editRating <= 3;
      if (requiresComment && !String(editComment || "").trim()) {
         setError(
            "Pentru o evaluare mai mică de 4 stele, te rugăm să adaugi un comentariu.",
         );
         return;
      }

      setSaving(true);
      setError("");

      try {
         const updated = await updateAdminRating(ratingId, {
            rating: editRating,
            comment: editComment,
         });

         setRatings((current) =>
            current.map((item) =>
               Number(item?.id) === ratingId
                  ? { ...item, ...(updated || {}), rating: editRating, comment: editComment }
                  : item,
            ),
         );
         closeEdit();
      } catch (err) {
         setError(err?.message || "Nu am putut salva recenzia.");
      } finally {
         setSaving(false);
      }
   };

   const listError = editingRating ? "" : error;
   const rootRef = useRef(null);

   return (
      <div className="adminReviewsPopup" ref={rootRef}>
         <div className="adminReviewsPopup__header">
            <h3 className="adminReviewsPopup__title">Recenzii</h3>
         </div>

         <div className="adminReviewsPopup__content">
            <div className="adminReviewsPopup__filters">
               <RatingStarsRow
                  value={starFilters}
                  previewValue={hoverFilter}
                  max={5}
                  interactive
                  multi
                  onChange={setStarFilters}
                  onPreviewChange={setHoverFilter}
               />

               <div
                  className="adminReviewsPopup__commentFilter"
                  role="tablist"
                  aria-label="Filtru comentariu"
               >
                  <button
                     type="button"
                     role="tab"
                     aria-selected={commentFilter === "all"}
                     className={
                        "adminReviewsPopup__commentFilterBtn" +
                        (commentFilter === "all" ? " is-active" : "")
                     }
                     onClick={() => setCommentFilter("all")}
                  >
                     Toate
                  </button>
                  <button
                     type="button"
                     role="tab"
                     aria-selected={commentFilter === "with"}
                     className={
                        "adminReviewsPopup__commentFilterBtn" +
                        (commentFilter === "with" ? " is-active" : "")
                     }
                     onClick={() => setCommentFilter("with")}
                  >
                     Cu text
                  </button>
                  <button
                     type="button"
                     role="tab"
                     aria-selected={commentFilter === "without"}
                     className={
                        "adminReviewsPopup__commentFilterBtn" +
                        (commentFilter === "without" ? " is-active" : "")
                     }
                     onClick={() => setCommentFilter("without")}
                  >
                     Fără text
                  </button>
               </div>
            </div>

            <div className="studentsProfileUI__reservationsMeta adminReviewsPopup__meta">
               <span className="studentsProfileUI__reservationsCount">
                  {sortedRatings.length} recenzii
                  {filterLabel ? ` · ${filterLabel}` : ""}
               </span>

               <button
                  type="button"
                  className="studentsProfileUI__sortBtn"
                  onClick={() => setNewestFirst((prev) => !prev)}
                  title={
                     newestFirst
                        ? "Sortează de la cele mai vechi"
                        : "Sortează de la cele mai noi"
                  }
                  aria-label={
                     newestFirst
                        ? "Sortează de la cele mai vechi"
                        : "Sortează de la cele mai noi"
                  }
               >
                  <ReactSVG
                     src={arrowIcon}
                     className={
                        "studentsProfileUI__sortIcon" +
                        (newestFirst ? " is-desc" : "")
                     }
                  />
               </button>
            </div>

            {loading ? (
               <div className="adminReviewsPopup__stateCard">Se încarcă...</div>
            ) : listError ? (
               <div className="adminReviewsPopup__stateCard adminReviewsPopup__stateCard--error">
                  {listError}
               </div>
            ) : !sortedRatings.length ? (
               <div className="adminReviewsPopup__stateCard">
                  {starFilters.length || commentFilter !== "all"
                     ? "Nu există recenzii pentru filtrul selectat."
                     : "Nu există recenzii."}
               </div>
            ) : (
               <div className="adminReviewsPopup__panel">
                  <div className="adminReviewsPopup__grid">
                     {visibleRatings.map((rating) => (
                        <ReviewCard
                           key={getRatingId(rating)}
                           rating={rating}
                           canReply={canReplyToRating(rating)}
                           canEdit={canEdit}
                           canDelete={canDelete}
                           deletingId={deletingId}
                           isNew={
                              seenBaselineMs !== null &&
                              getRatingCreatedMs(rating) > seenBaselineMs
                           }
                           onOpen={openView}
                           onEdit={openEdit}
                           onDelete={handleDelete}
                           onReply={handleReplyToReview}
                        />
                     ))}
                  </div>

                  {hasMore ? (
                     <button
                        type="button"
                        className="adminReviewsPopup__button adminReviewsPopup__button--secondary adminReviewsPopup__loadMore"
                        onClick={() =>
                           setVisibleCount((current) => current + PAGE_SIZE)
                        }
                     >
                        Vezi mai multe
                     </button>
                  ) : null}
               </div>
            )}
         </div>

         <AdminReviewsOverlayPortal hostRef={rootRef}>
            {viewingRating ? (
               <ReviewDetailOverlay
                  rating={viewingRating}
                  canReply={canReplyToRating(viewingRating)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  deletingId={deletingId}
                  isNew={
                     seenBaselineMs !== null &&
                     getRatingCreatedMs(viewingRating) > seenBaselineMs
                  }
                  onClose={closeView}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onReply={handleReplyToReview}
               />
            ) : null}

            {editingRating ? (
               <ReviewEditOverlay
                  rating={editingRating}
                  canDelete={canDelete}
                  editRating={editRating}
                  editHoverRating={editHoverRating}
                  editComment={editComment}
                  saving={saving}
                  deletingId={deletingId}
                  error={error}
                  onClose={closeEdit}
                  onSave={handleSaveEdit}
                  onDelete={handleDelete}
                  onEditRatingChange={setEditRating}
                  onEditHoverRatingChange={setEditHoverRating}
                  onEditCommentChange={setEditComment}
               />
            ) : null}
         </AdminReviewsOverlayPortal>
      </div>
   );
}
